import { useEffect, useRef, useMemo, useState, useContext } from 'react';
import { Outlet, NavLink, useLocation, useMatch } from 'react-router-dom';
import {
  useQueryState,
  useScroll,
  useFilters,
  Table,
  IconLoader,
  useGetList,
  SchemaFilterType,
  PermissionDenied,
  usePrevious,
  PermissionEnum,
  IconExpand,
  formatDate,
  IconGear,
  ClickOutsideDetector,
  ToggleInput,
  InputText,
  ButtonPrimary,
  ButtonSize,
  AccountingJobProps as AccountingJob,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetAccountingJobsQuery,
  useLazyGetAccountingJobsStatesQuery,
} from './store/service';
import {
  columns,
  schema,
  columnsLabels,
  filtersSchema,
} from './variables/list';
import { setTakeCount } from './store/slice';
import formatAccountingManagmentList from './helpers/format-accounting-jobs-list';
import { ActiveTabContext } from '../../../../../components/layout/context/active-tab-context';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const AccountingManagmentList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { focused } = useContext(ActiveTabContext);

  const prevFocused = usePrevious(focused);

  const [autoupdateEnabled, setAutoupdateEnabled] = useState(true);
  const [autoupdateTime, setAutoupdateTime] = useState('10');
  const [listAutoupdatedDate, setListAutoupdatedDate] = useState(new Date());
  const [autoupdateMenuVisible, setAutoupdateMenuVisible] = useState(false);
  const [autoupdateState, setAutoupdateState] = useState<{
    time: string;
    enabled: boolean;
  }>({
    time: '10',
    enabled: true,
  });

  const {
    accountingManagment: {
      pagination,
      takeCount,
      accountingJobs,
      accountingJobsErrorsList,
    },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const [getList, { isLoading, isFetching }] = useLazyGetAccountingJobsQuery();
  const [getAccountingJobsStates] = useLazyGetAccountingJobsStatesQuery();

  const match = useMatch(ROUTES.REPORTS_ACCOUNTING_MANAGMENT_LIST);

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<AccountingJob>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { search } = useLocation();

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string, time?: string) => {
    await onClearTimeout();

    timeout = setTimeout(
      async () => {
        setListAutoupdatedDate(new Date());

        await getAccountingJobsStates(query);
        await onGetStates(query, time || autoupdateState.time);
      },
      time ? Number(time) * 1000 : Number(autoupdateState.time) * 1000,
    );
  };

  const onApplyAutoupdate = () => {
    setTimeout(async () => {
      await onClearTimeout();
    });

    if (autoupdateEnabled && accountingJobs) {
      const ids = accountingJobs.map((item) => item.id);
      const query = `?ids=${ids.join('&ids=')}&take=${takeCount}`;

      onGetStates(query, autoupdateTime);
    }

    setAutoupdateState({
      time: autoupdateTime,
      enabled: autoupdateEnabled,
    });

    localStorage.setItem(
      'autoupdateEnabled',
      JSON.stringify({
        time: autoupdateTime,
        enabled: autoupdateEnabled,
      }),
    );

    setAutoupdateMenuVisible(false);
  };

  const formattedData = useMemo(() => {
    const data = formatAccountingManagmentList(
      accountingJobs as AccountingJob[],
    );

    return data;
  }, [accountingJobs, filters, sort]);

  useEffect(() => {
    if (match && autoupdateEnabled) {
      if (!focused && prevFocused) {
        setTimeout(async () => {
          await onClearTimeout();
        });
      } else if (
        focused &&
        prevFocused !== null &&
        !prevFocused &&
        accountingJobs
      ) {
        const ids = accountingJobs.map((item) => item.id);
        const query = `?ids=${ids.join('&ids=')}&take=${takeCount}`;
        onGetStates(query);
      }
    }
  }, [focused, prevFocused, match, autoupdateEnabled]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !accountingJobs &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
        sort,
      });
    }
  }, [filters, sort, accountingJobs, takeCount, activePermissions]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 47;

        dispatch(setTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    const autoupdateEnabledStorage = localStorage.getItem('autoupdateEnabled');

    if (autoupdateEnabledStorage) {
      const parsed = JSON.parse(autoupdateEnabledStorage);

      setAutoupdateEnabled(parsed.enabled);
      setAutoupdateTime(parsed.time);

      setAutoupdateState({
        time: parsed.time,
        enabled: parsed.enabled,
      });
    }
  }, []);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs.length &&
      match &&
      autoupdateState.enabled
    ) {
      const ids = accountingJobs.map((item) => item.id);
      const query = `?ids=${ids.join('&ids=')}&take=${takeCount}`;

      onGetStates(query);
    }
  }, [accountingJobs, match, autoupdateState]);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.accountingManagmentLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.accountingManagmentTableWrapper}>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={(name) => onSort(name)}
          filters={filters}
          sort={sort}
          columnsLabels={columnsLabels}
          onFilterChange={(filterValues) => onFilter(filterValues)}
          filtersComponent={filtersComponent}
          isLoading={isFetching || takeCount === 0 || isLoading}
          handleSubmit={handleSubmit}
          pagination={pagination}
          setPage={(data) => setPage({ ...data, filters })}
          take={takeCount.toString()}
          error={accountingJobsErrorsList || errorList}
          tableTopClassName={cn.accountingManagmentTableTop}
          topContent={
            <div className={cn.accountingManagmentTopContent}>
              {(activePermissions?.includes(
                PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
              ) ||
                activePermissions?.includes(
                  PermissionEnum.REPORTS_ACCOUNTING_JOBS_RESTATEMENT_CREATE_ACTION,
                )) && (
                <NavLink
                  className={cn.createJobLink}
                  to={`${ROUTES.REPORTS_ACCOUNTING_MANAGMENT_CREATE}${
                    search || ''
                  }`}
                >
                  <IconExpand width={16} height={16} />
                  Add Job
                </NavLink>
              )}
              <div className={cn.autoupdateWrapper}>
                <div className={cn.autoupdateTitle}>
                  {autoupdateState.enabled
                    ? `Auto‑refreshed: ${formatDate(
                        listAutoupdatedDate.toISOString(),
                        true,
                        true,
                      )}`
                    : 'Auto-update disabled'}
                </div>
                <ClickOutsideDetector
                  setOptionsListVisible={() => {
                    setAutoupdateMenuVisible(false);
                    setAutoupdateEnabled(autoupdateState.enabled);
                    setAutoupdateTime(autoupdateState.time);
                  }}
                >
                  <>
                    <button
                      type="button"
                      className={cn.autoupdateMenuButton}
                      onClick={() => {
                        if (autoupdateMenuVisible) {
                          setAutoupdateMenuVisible(false);
                          setAutoupdateEnabled(autoupdateState.enabled);
                          setAutoupdateTime(autoupdateState.time);
                        } else {
                          setAutoupdateMenuVisible(true);
                        }
                      }}
                    >
                      <IconGear />
                    </button>
                    {autoupdateMenuVisible && (
                      <div className={cn.autoupdateMenu}>
                        <div className={cn.toggleInputWrapper}>
                          <ToggleInput
                            checked={autoupdateEnabled}
                            onChange={(e) =>
                              setAutoupdateEnabled(e.target.checked)
                            }
                            title="Auto-update"
                            name="autoupdateEnabled"
                          />
                        </div>
                        <InputText
                          name="autoupdateTime"
                          title="Update interval (sec)"
                          value={autoupdateTime}
                          placeholder="Enter interval"
                          onChange={(e) => setAutoupdateTime(e.target.value)}
                          disabled={
                            !autoupdateState.enabled && !autoupdateEnabled
                          }
                        />
                        <ButtonPrimary
                          size={ButtonSize.SMALL}
                          text="Apply"
                          className={cn.applyAutoupdateButton}
                          onClick={onApplyAutoupdate}
                        />
                      </div>
                    )}
                  </>
                </ClickOutsideDetector>
              </div>
            </div>
          }
          onRowClick={() =>
            setTimeout(async () => {
              await onClearTimeout();
            })
          }
          clickable
          withScroll
        />
      </div>
    );
  }

  return (
    <div className={cn.accountingManagmentList} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default AccountingManagmentList;
